import { Fragment, useRef } from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@/components/IconButton';
import { DropDownProps } from '@/trio-ui/Dropdown/Dropdown.component';
import { OptionsWithLabel } from '@/types';
import TagOptionComponent from './TagOption.component';
import { TagSelectorOption } from './TagSelectorDropDown.component';
import {
  InputContainer,
  HiddenOption,
  Input,
  OptionContainer,
  DropDownOption,
  TagSelector,
  SelectorLabel,
  ContentContainer,
  ContentBox,
  RemoveButton,
  DropDownContainer,
  DropDown,
  ErrorOption,
  GroupLabel,
} from './TagSelectorDropDown.styles';
import WarningMessage from '../WarningMessage';

export type GroupedTagSelectorDropDownProps<Option> = Pick<
  DropDownProps,
  'name' | 'label' | 'onChangeDropDown' | 'isDisabled' | 'placeholder'
> & {
  allOptions: OptionsWithLabel<Option>[];
  filteredOptions: OptionsWithLabel<Option>[];
  errorMessage?: string;
  disableDeleteAll?: boolean;
  isTagSelectorOpened?: boolean;
  isLabelAboveField?: boolean;
  selectorLimit?: number;
  selectedOptions?: string[];
  inputValue?: string;
  toggleTagSelectorHandler?: () => void;
  selectedOptionsHandler?: (option: string) => void;
  onRemoveItem?: (index: number) => void;
  removeAllOptionsHandler?: () => void;
};

export const isTagSelectorOption = (
  option: TagSelectorOption | string
): option is TagSelectorOption => {
  return typeof option !== 'string';
};

const GroupedTagSelectorDropDownComponent = function <Option extends TagSelectorOption | string>({
  name,
  label,
  placeholder,
  selectorLimit,
  onChangeDropDown,
  allOptions = [],
  filteredOptions = [],
  isDisabled,
  disableDeleteAll,
  toggleTagSelectorHandler,
  isTagSelectorOpened,
  isLabelAboveField,
  inputValue = '',
  selectedOptionsHandler,
  selectedOptions = [],
  onRemoveItem,
  removeAllOptionsHandler,
  errorMessage,
  ...props
}: GroupedTagSelectorDropDownProps<Option>): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);

  const getOrHideInput = function (chosenOptions: string[]): JSX.Element {
    if (selectorLimit === undefined || chosenOptions.length < selectorLimit) {
      return (
        <InputContainer>
          {!isDisabled && (
            <IconButton
              onClick={() => {
                toggleTagSelectorHandler?.();
                inputRef.current?.focus();
              }}
              disabled={isDisabled}
              type="button"
              icon={AddIcon}
            />
          )}
          <Input
            ref={inputRef}
            data-testid={`${name}-enabled`}
            name={name}
            type="text"
            value={inputValue}
            disabled={isDisabled}
            autoComplete="off"
            placeholder={chosenOptions.length ? '' : placeholder}
            id="tag-selector-input"
            onFocus={toggleTagSelectorHandler}
            onChange={onChangeDropDown}
          />
        </InputContainer>
      );
    }

    return <></>;
  };

  const renderOption = function (option: Option, index: number): JSX.Element {
    const [optionValue, optionLabel, backgroundColor] = isTagSelectorOption(option)
      ? [option.value, option.label, option.backgroundColor]
      : [option, option, undefined];

    if (selectedOptions.includes(optionValue)) {
      return <HiddenOption key={`hidden-option-${name}-${index}`} />;
    }

    return (
      <OptionContainer key={`${name}-option-${index}`}>
        <DropDownOption
          $backgroundColor={backgroundColor}
          type="button"
          onClick={() => selectedOptionsHandler?.(optionValue)}
        >
          {optionLabel}
        </DropDownOption>
      </OptionContainer>
    );
  };

  const renderOptionsGroup = (group: OptionsWithLabel<Option>): JSX.Element => {
    return (
      <Fragment key={group.label}>
        <GroupLabel>{group.label}</GroupLabel>
        {group.options.map(renderOption)}
      </Fragment>
    );
  };

  return (
    <TagSelector
      {...props}
      isLabelAboveField={isLabelAboveField}
      data-testid={`dropdown-${name}-btn`}
      onKeyDown={(event) => {
        if (event.keyCode === 27) {
          toggleTagSelectorHandler?.();
        }
      }}
    >
      <SelectorLabel htmlFor={name} isLabelAboveField={isLabelAboveField}>
        {label}
      </SelectorLabel>
      <ContentContainer id="tag-selector" isLabelAboveField={isLabelAboveField}>
        <ContentBox hasAnError={!!errorMessage}>
          {selectedOptions.length > 0 &&
            selectedOptions.map((selectedOption, index) => {
              let filteredOption = selectedOption;
              let backgroundColor: string | undefined;

              const foundOption = allOptions
                .find((group) =>
                  group.options.find(
                    (option) =>
                      (isTagSelectorOption(option) ? option.value : option) === selectedOption
                  )
                )
                ?.options.find(
                  (option) =>
                    (isTagSelectorOption(option) ? option.value : option) === selectedOption
                );

              if (foundOption) {
                filteredOption = isTagSelectorOption(foundOption)
                  ? foundOption.displayLabel || foundOption.label
                  : foundOption;

                if (isTagSelectorOption(foundOption)) {
                  backgroundColor = foundOption.backgroundColor;
                }
              }

              return (
                <TagOptionComponent
                  key={`${name}-option-${index.toString()}`}
                  optionLabel={filteredOption}
                  onRemoveItem={() => onRemoveItem?.(index)}
                  isDisabled={isDisabled}
                  backgroundColor={backgroundColor}
                />
              );
            })}

          {getOrHideInput(selectedOptions)}

          {!disableDeleteAll && selectedOptions.length > 0 && (
            <RemoveButton type="button" onClick={removeAllOptionsHandler}>
              <CloseIcon sx={{ fontSize: '14px', fill: '#FFF' }} />
            </RemoveButton>
          )}
        </ContentBox>

        <DropDownContainer>
          {isTagSelectorOpened && (
            <>
              <DropDown data-testid={`${name}-options`}>
                {!filteredOptions?.length && (
                  <ErrorOption data-testid="searchQuery-error">No options found</ErrorOption>
                )}
                {filteredOptions?.map(renderOptionsGroup)}
              </DropDown>
            </>
          )}
        </DropDownContainer>

        <WarningMessage errorMessage={errorMessage} />
      </ContentContainer>
    </TagSelector>
  );
};

export default GroupedTagSelectorDropDownComponent;
